import React, { useState, useEffect } from "react";
import { api } from "../../services/api";

const StudentProgressStats = () => {
  const [stats, setStats] = useState({
    totalStudents: 0,
    activeStudents: 0,
    totalPracticeQuestions: 0,
    averageProgress: 0,
    examsCompleted: 0,
    quizzesCompleted: 0,
    averageScore: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProgressStats = async () => {
      try {
        // Fetch all users first
        const usersResponse = await api.users.getAll();

        if (!usersResponse.success) {
          throw new Error(usersResponse.message || "Failed to fetch users");
        }

        // Filter to only include students
        const students = usersResponse.data.filter(
          (user) => user.role === "USER" || user.role === "STUDENT"
        );

        if (students.length === 0) {
          setLoading(false);
          return;
        }

        // Fetch progress for each student
        let progressData = [];
        for (const student of students) {
          try {
            const response = await api.userProgress.getByUserId(student.id);
            if (response.success && response.data) {
              progressData.push(response.data);
            }
          } catch (error) {
            console.error(
              `Error fetching progress for user ${student.id}:`,
              error
            );
          }
        }

        // Calculate statistics
        const totalStudents = students.length;
        const activeStudents = progressData.filter(
          (progress) =>
            progress.totalPracticeQuestions > 0 ||
            progress.totalExamsTaken > 0 ||
            progress.totalQuizzesTaken > 0
        ).length;

        const totalPracticeQuestions = progressData.reduce(
          (sum, progress) => sum + (progress.totalPracticeQuestions || 0),
          0
        );

        const averageProgress =
          progressData.length > 0
            ? Math.round(
                progressData.reduce(
                  (sum, progress) => sum + (progress.overallProgress || 0),
                  0
                ) / progressData.length
              )
            : 0;

        const examsCompleted = progressData.reduce(
          (sum, progress) => sum + (progress.totalExamsTaken || 0),
          0
        );

        const quizzesCompleted = progressData.reduce(
          (sum, progress) => sum + (progress.totalQuizzesTaken || 0),
          0
        );

        const averageScore =
          progressData.filter((p) => p.averageScore > 0).length > 0
            ? Math.round(
                progressData
                  .filter((p) => p.averageScore > 0)
                  .reduce((sum, progress) => sum + progress.averageScore, 0) /
                  progressData.filter((p) => p.averageScore > 0).length
              )
            : 0;

        setStats({
          totalStudents,
          activeStudents,
          totalPracticeQuestions,
          averageProgress,
          examsCompleted,
          quizzesCompleted,
          averageScore,
        });
      } catch (error) {
        console.error("Error fetching progress statistics:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProgressStats();
  }, []);

  return (
    <div className="col-xxl-4 col-md-6">
      <div className="card">
        <div className="card-header">
          <div className="d-flex align-items-center flex-wrap gap-2 justify-content-between">
            <h6 className="mb-2 fw-bold text-lg mb-0">
              Student Progress Summary
            </h6>
          </div>
        </div>
        <div className="card-body">
          {loading ? (
            <div className="text-center py-4">Loading statistics...</div>
          ) : (
            <div className="d-flex flex-column gap-3">
              <div className="d-flex justify-content-between">
                <span className="text-secondary-light">Total Students:</span>
                <span className="fw-bold">{stats.totalStudents}</span>
              </div>
              <div className="d-flex justify-content-between">
                <span className="text-secondary-light">Active Students:</span>
                <span className="fw-bold">{stats.activeStudents}</span>
              </div>
              <div className="d-flex justify-content-between">
                <span className="text-secondary-light">Average Progress:</span>
                <span className="fw-bold">{stats.averageProgress}%</span>
              </div>
              <div className="d-flex justify-content-between">
                <span className="text-secondary-light">Exams Completed:</span>
                <span className="fw-bold">{stats.examsCompleted}</span>
              </div>
              <div className="d-flex justify-content-between">
                <span className="text-secondary-light">Quizzes Completed:</span>
                <span className="fw-bold">{stats.quizzesCompleted}</span>
              </div>
              <div className="d-flex justify-content-between">
                <span className="text-secondary-light">
                  Practice Questions:
                </span>
                <span className="fw-bold">{stats.totalPracticeQuestions}</span>
              </div>
              <div className="d-flex justify-content-between">
                <span className="text-secondary-light">Average Score:</span>
                <span className="fw-bold">{stats.averageScore}%</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StudentProgressStats;
